/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { UTC } from 'rewire-common';
import { observable, freeze } from 'rewire-core';
import {
  DeepPartial,
  IArea,
  IGroup,
  IUser,
  IView,
  IViewOptions,
  ILocation,
  ITimeFormat,
  MessageFilterInput,
  AreaSort,
  ViewFilter,
  ViewSort,
  IScheduleViewOptions,
} from '../DataTypes';
import { DateDuration } from '../ui/DateRangeNavModel';
import { IDateSelector } from '../ui/MainCustomFormDatePicker';
import { getTimeFormat, setTimeFormat, TimeFormat, localTimeZoneUTCNow } from '../utility/DateUtils';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import udtsSubscriptionService from '../services/UdtsSubscriptionService';
import { subscribeToMessages } from '../services/MessagesSubscriptionService';
import { notifications } from '../ui/Notifications';
import authService from '../services/AuthService';
import DataQueries from '../graphql/Queries';
import { getInitials } from '../utility/StringUtils';

interface IMainPageModelState {
  areas: DeepPartial<IArea>[];
  areaMap: { [id: string]: { id: string; name: string; code: string; groups: DeepPartial<IGroup>[] } };
  groupMap: {
    [id: string]: {
      id: string;
      name: string;
      code: string;
      position: number;
      scheduleOptions?: IScheduleViewOptions;
      groupLinkData: any;
    };
  };
  viewMap: { [id: string]: { id: string; name: string; code: string; fields?: object; options?: IViewOptions; viewLinkData: any } };
  views: DeepPartial<IView>[];
  user: IUser;
  isLoaded: boolean;
  dateSelector: IDateSelector;
  dateDuration: DateDuration;
  testModeEnabled: boolean;
  // locationName?: string;
}

type IAreaMap = IMainPageModelState['areaMap'];
type IGroupMap = IMainPageModelState['groupMap'];
type IGroupMapItem = IGroupMap[''];
type IViewMap = IMainPageModelState['viewMap'];
type IViewMapItem = IViewMap[''];

export class MainPageModel {
  state: IMainPageModelState;
  private _messagesSubscription: any;

  constructor() {
    this.state = observable({
      areas: [],
      areaMap: {} as IAreaMap,
      groupMap: {} as IGroupMap,
      views: [],
      viewMap: {} as IViewMap,
      user: {} as IUser,
      isLoaded: false,
      dateSelector: 'Today',
      dateDuration: '1 Week',
      testModeEnabled: false,
      // locationName: undefined,
    });
  }

  reset() {
    udfsSubscriptionService.subscriptionModel.reset();
    udtsSubscriptionService.subscriptionModel.reset();
    this._messagesSubscription?.unsubscribe();
    this._messagesSubscription = undefined;
    freeze(() => {
      this.state.areas = [];
      this.state.areaMap = {};
      this.state.groupMap = {};
      this.state.views = [];
      this.state.viewMap = {};
      this.state.user = {} as IUser;
      this.state.isLoaded = false;
      this.state.dateSelector = 'Today';
      this.state.dateDuration = '1 Week';
      this.state.testModeEnabled = false;
      // this.state.locationName = undefined;
    });
  }

  load = async () => {
    this.state.isLoaded = false;
    await this.loadData();
    await udfsSubscriptionService.subscriptionModel.startUpdate();
    await udtsSubscriptionService.subscriptionModel.startUpdate();
    this.state.isLoaded = true;
  };

  loadData = async () => {
    const user = await authService.getUserInfo();
    if (!user) return;
    const results = await Promise.all([this.getAreasData(), this.getViewsData()]);

    const areas = results[0] as IArea[];
    const views = results[1] as IView[];

    const { areaMap, groupMap } = this.getAreaAndGroupMaps(areas);
    const viewMap = this.getViewMap(views);

    this.configureSystemMessagesSubscription();

    const dateSelector = (localStorage.getItem(`${user.id!}-dateSelector`) || 'Today') as IDateSelector;
    const dateDuration = (localStorage.getItem(`${user.id!}-dateDuration`) || '1 Week') as DateDuration;
    const testModeEnabled = (JSON.parse(localStorage.getItem(`${user.id!}-testModeEnabled`) ?? 'null') ?? false) as boolean;

    freeze(() => {
      this.state.areas = areas;
      this.state.areaMap = areaMap;
      this.state.groupMap = groupMap;
      this.state.views = views;
      this.state.viewMap = viewMap;
      this.state.user = user as IUser;
      this.state.dateSelector = dateSelector;
      this.state.dateDuration = dateDuration;
      this.state.testModeEnabled = testModeEnabled;
      // this.state.locationName = user.location.name;

      this.updateTimeFormat((user.location as ILocation).timeFormat);
    });
  };

  configureSystemMessagesSubscription() {
    const systemMessageSchema = `
      id
      text
      level
      type
      effective
    `;
    const systemMessageFilter: MessageFilterInput = {
      types: ['system'],
    };

    if (!this._messagesSubscription) {
      this._messagesSubscription = subscribeToMessages(systemMessageSchema, systemMessageFilter, (msg: any) => {
        if (msg?.operation === 'added') {
          const message = msg.message;
          if (!message) return;
          if (message.level === 'error') {
            notifications.error(message.text);
          } else if (message.level === 'warning') {
            notifications.warning(message.text);
          } else {
            notifications.info(message.text);
          }
        }
      });
    }
  }

  updateTimeFormat(locationTimeFormat?: ITimeFormat) {
    if (!locationTimeFormat) return;

    const timeFormat: TimeFormat | undefined = getTimeFormat(locationTimeFormat);
    if (timeFormat) setTimeFormat(timeFormat);
  }

  getAreasData = async (): Promise<DeepPartial<IArea>[]> => {
    const returnSchema = `
      id
      name
      code
      groups {
        id
        name
        code
        position
        scheduleOptions
        area {
          id
          name
        }
      }
    `;

    const sort: AreaSort = { name: { direction: 'asc' } };
    const result = await DataQueries.getAreas(returnSchema, undefined, sort, 200);
    return result && result.data;
  };

  getViewsData = async (): Promise<DeepPartial<IView>[]> => {
    const returnSchema = `
      id
      name
      code
      fields
      options
    `;
    const filter: ViewFilter = { type: 'view' };
    const sort: ViewSort = { name: { direction: 'asc' } };
    const result = await DataQueries.getViews(returnSchema, filter, sort);
    return result && result.data;
  };

  getAreaAndGroupMaps(areas: IArea[]) {
    const areaMap: IAreaMap = {};
    const groupMap: IGroupMap = {};

    areas.forEach((area: DeepPartial<IArea>) => {
      area.groups!.sort((a: IGroup, b: IGroup) => a.name.localeCompare(b.name));
      areaMap[`${area.id}`] = { id: area.id!, name: area.name!, code: area.code!, groups: area.groups! };
      area.groups!.forEach((group: DeepPartial<IGroup>) => {
        const groupScheduleFilters = group.scheduleOptions?.filters ?? {};
        const groupScheduleGroups = group.scheduleOptions?.groups ?? [];
        const groupScheduleSorts = group.scheduleOptions?.sorts ?? {};
        const groupScheduleGridOptions = group.scheduleOptions?.scheduleGridOptions;
        const groupLinkData = {
          areaId: area.id,
          groupId: group.id,
          filters: { ...groupScheduleFilters, areaIds: [area.id], groupIds: [group.id] },
          groups: [...groupScheduleGroups],
          sorts: { ...groupScheduleSorts },
          scheduleGridOptions: groupScheduleGridOptions ? { ...groupScheduleGridOptions } : undefined,
          selectedEmployeeId: undefined,
          selectedBatchId: undefined,
        };
        groupMap[`${group.id}`] = {
          id: group.id!,
          name: group.name!,
          code: group.code!,
          position: group.position!,
          areaId: area.id,
          scheduleOptions: group.scheduleOptions,
          groupLinkData: groupLinkData,
        } as IGroupMapItem;
      });
    });

    return { areaMap, groupMap };
  }

  getViewMap(views: IView[]) {
    const viewMap: IViewMap = {};

    views.forEach((view: DeepPartial<IView>) => {
      const scheduleGridOptions = (view.fields as any)?.scheduleGridOptions;
      const reportSecondaryFilters = (view.fields as any)?.reportSecondaryFilters;
      const viewLinkData = {
        viewId: view.id,
        scheduleGridOptions: scheduleGridOptions ? { ...scheduleGridOptions } : undefined,
        reportSecondaryFilters: reportSecondaryFilters ? { ...reportSecondaryFilters } : undefined,
        ...view.options,
      } as any;

      viewMap[`${view.id}`] = {
        id: view.id!,
        name: view.name!,
        code: view.code!,
        fields: view.fields,
        options: view.options,
        viewLinkData: viewLinkData,
      } as IViewMapItem;
    });

    return viewMap;
  }

  updateUser = async () => {
    const user = await authService.getUserInfo();
    if (!user) {
      return;
    }
    this.state.user = user as IUser;
  };

  updateLocation = async () => {
    await this.updateUser();
  };

  updateAreasAndGroups = async () => {
    const areas = (await this.getAreasData()) as IArea[];
    const { areaMap, groupMap } = this.getAreaAndGroupMaps(areas);

    freeze(() => {
      this.state.areas = [];
      this.state.areaMap = {};
      this.state.groupMap = {};
    });
    freeze(() => {
      this.state.areas = areas;
      this.state.areaMap = areaMap;
      this.state.groupMap = groupMap;
    });
  };

  updateViews = async () => {
    const user = await authService.getUserInfo();
    if (!user) return;

    const views = (await this.getViewsData()) as IView[];
    const viewMap = this.getViewMap(views);

    freeze(() => {
      this.state.views = [];
      this.state.viewMap = {};
    });
    freeze(() => {
      this.state.views = views;
      this.state.viewMap = viewMap;
    });
  };

  locationTimeZoneUTCNow(): UTC {
    if (!this.locationTimeZone) {
      return localTimeZoneUTCNow();
    }
    return UTC.nowtz(this.locationTimeZone);
  }

  toggleTestMode() {
    this.testModeEnabled = !this.testModeEnabled;
  }

  get areas() {
    return this.state.areas;
  }

  get areaMap() {
    return this.state.areaMap;
  }

  get groupMap() {
    return this.state.groupMap;
  }

  get viewMap() {
    return this.state.viewMap;
  }

  get views() {
    return this.state.views;
  }

  get user() {
    return this.state.user;
  }

  get userId() {
    return this.user.id;
  }

  get avatar() {
    return this.user.avatar;
  }

  get userInitials(): string | undefined {
    return getInitials(this.user.name);
  }

  get location() {
    return this.user.location;
  }

  get locationTimeZone() {
    return this.location?.tz?.id;
  }

  get scheduleAccess() {
    return authService.evalSecurityToken('view_time');
  }

  get canEditSchedule() {
    return authService.evalSecurityToken('edit_time');
  }

  get scheduleViewOnly() {
    return !this.canEditSchedule && this.scheduleAccess;
  }

  get timeOffRequestAccess() {
    return authService.evalSecurityToken('view_timeoff');
  }

  get canAddTimeOffRequests() {
    return authService.evalSecurityToken('create_delete_timeoff');
  }

  get canEditTimeOffRequests() {
    return authService.evalSecurityToken('edit_timeoff');
  }

  get canDeleteTimeOffRequests() {
    return authService.evalSecurityToken('create_delete_timeoff');
  }

  get timecardAccess() {
    return authService.evalSecurityToken('view_time');
  }

  get analyticsAccess() {
    return authService.evalSecurityToken('view_analytics');
  }

  get canEditTime() {
    return authService.evalSecurityToken('edit_time');
  }

  get timecardViewOnly() {
    return !this.canEditTime && this.timecardAccess;
  }

  get batchAccess() {
    return this.canViewBatches && !this.testModeEnabled;
  }

  get canViewBatches() {
    return authService.evalSecurityToken('view_batches');
  }

  get canAddBatches() {
    return authService.evalSecurityToken('create_delete_batches');
  }

  get canEditBatches() {
    return authService.evalSecurityToken('edit_batches');
  }

  get canDeleteBatches() {
    return authService.evalSecurityToken('create_delete_batches');
  }

  get reportAccess() {
    return authService.evalSecurityToken('view_report');
  }

  get testAccess() {
    return this.canViewTests && this.testModeEnabled;
  }

  get canViewTests() {
    return authService.evalSecurityToken('view_tests');
  }

  get canEditTests() {
    return authService.evalSecurityToken('edit_tests');
  }

  get testViewOnly() {
    return !this.canEditTests && this.canViewTests;
  }

  get canToggleTestMode() {
    return this.canViewTests;
  }

  get testModeEnabled() {
    return this.state.testModeEnabled;
  }

  set testModeEnabled(value: boolean) {
    localStorage.setItem(`${this.userId}-testModeEnabled`, JSON.stringify(value));
    this.state.testModeEnabled = value;
  }

  get canAddUsers() {
    return authService.evalSecurityToken('create_delete_users');
  }

  get canEditUsers() {
    return authService.evalSecurityToken('edit_users');
  }

  get canDeleteUsers() {
    return authService.evalSecurityToken('create_delete_users');
  }

  get rolesAccess() {
    return authService.evalSecurityToken('view_roles');
  }

  get canAddRoles() {
    return authService.evalSecurityToken('create_delete_roles');
  }

  get canEditRoles() {
    return authService.evalSecurityToken('edit_roles');
  }

  get canDeleteRoles() {
    return authService.evalSecurityToken('create_delete_roles');
  }

  get canAddEmployees() {
    return authService.evalSecurityToken('create_delete_employees');
  }

  get canEditEmployees() {
    return authService.evalSecurityToken('edit_employees');
  }

  get canDeleteEmployees() {
    return authService.evalSecurityToken('create_delete_employees');
  }

  get canAddLocations() {
    /** Disable the ability to add locations through the ui at this time */
    return false;
    // return authService.evalSecurityToken('create_delete_locations');
  }

  get canEditLocations() {
    return authService.evalSecurityToken('edit_locations');
  }

  get canDeleteLocations() {
    /** Disable the ability to delete locations through the ui at this time */
    return false;
    // return authService.evalSecurityToken('create_delete_locations');
  }

  get canAddAreas() {
    return authService.evalSecurityToken('create_delete_areas');
  }

  get canEditAreas() {
    return authService.evalSecurityToken('edit_areas');
  }

  get canDeleteAreas() {
    return authService.evalSecurityToken('create_delete_areas');
  }

  get canAddGroups() {
    return authService.evalSecurityToken('create_delete_groups');
  }

  get canEditGroups() {
    return authService.evalSecurityToken('edit_groups');
  }

  get canDeleteGroups() {
    return authService.evalSecurityToken('create_delete_groups');
  }

  get canAddJobs() {
    return authService.evalSecurityToken('create_delete_jobs');
  }

  get canEditJobs() {
    return authService.evalSecurityToken('edit_jobs');
  }

  get canDeleteJobs() {
    return authService.evalSecurityToken('create_delete_jobs');
  }

  get canAddOccupations() {
    return authService.evalSecurityToken('create_delete_occupations');
  }

  get canEditOccupations() {
    return authService.evalSecurityToken('edit_occupations');
  }

  get canDeleteOccupations() {
    return authService.evalSecurityToken('create_delete_occupations');
  }

  get canAddTemplates() {
    return authService.evalSecurityToken('create_delete_templates');
  }

  get canEditTemplates() {
    return authService.evalSecurityToken('edit_templates');
  }

  get canDeleteTemplates() {
    return authService.evalSecurityToken('create_delete_templates');
  }

  get canAddGenerators() {
    return authService.evalSecurityToken('create_delete_generators');
  }

  get canEditGenerators() {
    return authService.evalSecurityToken('edit_generators');
  }

  get canDeleteGenerators() {
    return authService.evalSecurityToken('create_delete_generators');
  }

  get canAddReasons() {
    return authService.evalSecurityToken('create_delete_reasons');
  }

  get canEditReasons() {
    return authService.evalSecurityToken('edit_reasons');
  }

  get canDeleteReasons() {
    return authService.evalSecurityToken('create_delete_reasons');
  }

  get canAddTags() {
    return authService.evalSecurityToken('edit_core');
  }

  get canEditTags() {
    return authService.evalSecurityToken('edit_core');
  }

  get canViewTimeOffBalances() {
    return authService.evalSecurityToken('view_timeoff_balances');
  }

  get canViewTimeOffTypes() {
    return authService.evalSecurityToken('view_timeoff_balances');
  }

  get canAddTimeOffTypes() {
    return authService.evalSecurityToken('create_delete_timeoff_balances');
  }

  get canEditTimeOffTypes() {
    return authService.evalSecurityToken('edit_timeoff_balances');
  }

  get canDeleteTimeOffTypes() {
    return authService.evalSecurityToken('create_delete_timeoff_balances');
  }

  get canViewTimeOffAccruals() {
    return authService.evalSecurityToken('view_timeoff_balances');
  }

  get canAddTimeOffAccruals() {
    return authService.evalSecurityToken('create_delete_timeoff_balances');
  }

  get canEditTimeOffAccruals() {
    return authService.evalSecurityToken('edit_timeoff_balances');
  }

  get canDeleteTimeOffAccruals() {
    return authService.evalSecurityToken('create_delete_timeoff_balances');
  }

  get canAddUdfs() {
    return authService.evalSecurityToken('create_delete_udfs');
  }

  get canEditUdfs() {
    return authService.evalSecurityToken('edit_udfs');
  }

  get canDeleteUdfs() {
    return authService.evalSecurityToken('create_delete_udfs');
  }

  get canAddUdts() {
    return authService.evalSecurityToken('create_delete_udts');
  }

  get canEditUdts() {
    return authService.evalSecurityToken('edit_udts');
  }

  get canDeleteUdts() {
    return authService.evalSecurityToken('create_delete_udts');
  }

  get canAddViews() {
    return authService.evalSecurityToken('create_delete_views');
  }

  get canEditViews() {
    return authService.evalSecurityToken('edit_views');
  }

  get canDeleteViews() {
    return authService.evalSecurityToken('create_delete_views');
  }

  get canEditAccountSettings() {
    return authService.evalSecurityToken('edit_users');
  }

  get canViewBilling() {
    return this.canEditBilling;
  }

  get canEditBilling() {
    return !!this.userId && !!this.location.planAdministrator?.id && this.userId === this.location.planAdministrator?.id;
  }

  get automationRulesAccess() {
    return false;
    // Token doesn't exist
    // return authService.evalSecurityToken('view_automation_rules');
  }

  get canEditAutomationRules() {
    return false;
    // Token doesn't exist
    // return authService.evalSecurityToken('edit_automation_rules');
  }

  get triggerRulesAccess() {
    return false;
    // Token doesn't exist
    // return authService.evalSecurityToken('view_trigger_rules');
  }

  get canEditTriggerRules() {
    return false;
    // Token doesn't exist
    // return authService.evalSecurityToken('edit_trigger_rules');
  }

  get timecardRulesAccess() {
    return authService.evalSecurityToken('view_timecard_rules');
  }

  get canEditTimecardRules() {
    return authService.evalSecurityToken('edit_timecard_rules');
  }

  get timecardExporterRulesAccess() {
    return authService.evalSecurityToken('view_timecard_exporter_rules');
  }

  get canEditTimecardExporterRules() {
    return authService.evalSecurityToken('edit_timecard_exporter_rules');
  }

  get validationRulesAccess() {
    return authService.evalSecurityToken('view_validation_rules');
  }

  get canEditValidationRules() {
    return authService.evalSecurityToken('edit_validation_rules');
  }

  get timeOffBalancesRulesAccess() {
    return authService.evalSecurityToken('view_timeoff_balances_rules');
  }

  get canEditTimeOffBalancesRules() {
    return authService.evalSecurityToken('edit_timeoff_balances_rules');
  }

  get apiAccess() {
    return authService.evalSecurityToken('edit_apis');
  }

  get canSwitchLocation() {
    return authService.evalSecurityToken('switch_location');
  }

  get canAddOrganizations() {
    return authService.evalSecurityToken('create_delete_organizations');
  }

  get canEditOrganizations() {
    return authService.evalSecurityToken('edit_organizations');
  }

  get canDeleteOrganizations() {
    return authService.evalSecurityToken('create_delete_organizations');
  }

  get postedDate() {
    return this.location?.postedDate;
  }

  get dateSelector(): IDateSelector {
    return this.state.dateSelector;
  }

  set dateSelector(value: IDateSelector) {
    localStorage.setItem(`${this.userId}-dateSelector`, value);
    this.state.dateSelector = value;
  }

  get dateDuration(): DateDuration {
    return this.state.dateDuration;
  }

  set dateDuration(value: DateDuration) {
    localStorage.setItem(`${this.userId}-dateDuration`, value);
    this.state.dateDuration = value;
  }

  // get locationName() {
  //   return this.state.locationName;
  // }

  get isLoaded() {
    return this.state.isLoaded;
  }
}

const mainPageModel = new MainPageModel();
export default mainPageModel;
